// Lógica de "Meu Trabalho Hoje" e enriquecimento dos cards do Kanban (spec do Bruno — doc 01).
// Funções puras, derivadas do estado já carregado da negociação — sem queries adicionais.
import { Negociacao } from '../../../types';
import { Modalidade, normalizarModalidade } from '../../../src/lib/modalidade';
import { isPendenciaDocumental, ultimaDecisaoAnalise } from '../../../src/lib/analiseCadastralUtils';

export { isPendenciaDocumental } from '../../../src/lib/analiseCadastralUtils';

/** Limiar de kWh acima do qual um lead em fechamento é considerado de alta prioridade (P1). */
export const KWH_ALTO = 2000;
/** Dias sem interação para um lead ser considerado "Sem interação" (esfriando) — doc 01. */
export const DIAS_SEM_INTERACAO = 14;

const startOfToday = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const isoHoje = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Data da última interação efetiva (atividade > updated_at como fallback). */
const ultimaInteracao = (n: Negociacao): number => {
    const t = (n as any).ultima_atividade_at || n.updated_at;
    return t ? new Date(t).getTime() : 0;
};

/** Dias inteiros desde a última interação. */
export const diasSemInteracao = (n: Negociacao): number => {
    const base = ultimaInteracao(n);
    if (!base) return 0;
    return Math.floor((Date.now() - base) / 86400000);
};

/** Dias inteiros desde a criação. */
export const diasDesdeCriacao = (n: Negociacao): number => {
    if (!n.created_at) return 0;
    return Math.floor((Date.now() - new Date(n.created_at).getTime()) / 86400000);
};

const STATUS_ENCERRADOS = ['perdido', 'termo_reprovado', 'ganho', 'termo_aprovado'];

/**
 * Selo do card quando a analista já decidiu e devolveu ao vendedor. Sem isso,
 * "Termo em análise" valia tanto para a fila de análise quanto para a pendência
 * documental (pedido de 20/10/2026). Reenviada para reanálise ela volta a
 * `termo_assinado` e o selo some.
 */
export const pendenciaDaAnalise = (n: Negociacao): 'Pendência documental' | 'Exigir garantia' | null => {
    if (n.status !== 'termo_analise') return null;
    const decisao = ultimaDecisaoAnalise(n);
    if (decisao === 'Pendência Documental') return 'Pendência documental';
    if (decisao === 'Exigir Garantia') return 'Exigir garantia';
    return null;
};

/** Termo pendente de assinatura. */
export const isTermoPendente = (n: Negociacao): boolean => n.status === 'termo_enviado';

/** Sem interação há ≥ DIAS_SEM_INTERACAO dias (exceto leads encerrados/perdidos/reprovados). */
export const isSemInteracao = (n: Negociacao): boolean => {
    if (STATUS_ENCERRADOS.includes(n.status)) return false;
    return diasSemInteracao(n) >= DIAS_SEM_INTERACAO;
};

/** Entrou em "Simulação Feita" hoje: simulação gerada hoje (url_simulacao) ou stage de simulação mudado hoje. */
export const entrouSimulacaoHoje = (n: Negociacao): boolean => {
    const hoje = isoHoje();
    const sims = n.url_simulacao;
    if (Array.isArray(sims) && sims.some(s => (s?.data_criacao || '').slice(0, 10) === hoje)) return true;
    // fallback: virou simulação realizada hoje
    if (n.status === 'simulacao_realizada' && (n.stage_changed_at || '').slice(0, 10) === hoje) return true;
    return false;
};

/** Nº de visitas concluídas hoje neste lead (para o medidor de Meta do Dia).
 * Toda conclusão conta, independente da origem — tarefa criada sem "Tipo da
 * Visita" (tipo NULL, ex.: direto no Kanban) também entra. */
export const visitasConcluidasHoje = (n: Negociacao): number => {
    const tarefas: any[] = (n as any).tarefas || [];
    const inicio = startOfToday().getTime();
    return tarefas.filter(t =>
        t && t.status === 'concluido' && t.data_conclusao &&
        new Date(t.data_conclusao).getTime() >= inicio
    ).length;
};

/** Próxima ação padrão derivada do status (sobrescrita por n.proxima_acao quando preenchida). */
export const proximaAcaoPadrao = (n: Negociacao): string => {
    if (n.proxima_acao && n.proxima_acao.trim()) return n.proxima_acao.trim();
    if (isPendenciaDocumental(n)) return 'Solicitar documento faltante';
    switch (n.status) {
        case 'em_andamento': return 'Avançar contato';
        case 'simulacao_realizada': return 'Apresentar proposta';
        case 'termo_enviado': return 'Cobrar assinatura';
        case 'termo_analise': return 'Acompanhar análise';
        case 'termo_assinado': return 'Acompanhar aprovação';
        default: return 'Avançar contato';
    }
};

export type ScorePrioridade = 'P1' | 'P2' | 'P3';

/** Score de prioridade automático (sobrescrito por n.score_prioridade quando preenchido). */
export const scorePrioridade = (n: Negociacao): ScorePrioridade => {
    if (n.score_prioridade) return n.score_prioridade;
    const altoKwh = (n.consumo_medio_kwh || 0) >= KWH_ALTO;
    const emFechamento = n.status === 'termo_enviado' || n.status === 'termo_assinado';
    // P1: esfriando OU etapa próxima do fechamento com alto kWh
    if (isSemInteracao(n) || (emFechamento && altoKwh)) return 'P1';
    // P3: prospecção recente e baixo valor
    if (n.status === 'em_andamento' && diasDesdeCriacao(n) <= 7 && !altoKwh) return 'P3';
    // P2: demais (etapa intermediária, dentro do prazo)
    return 'P2';
};

/** Lead exige ação: esfriando (sem interação) ou em fechamento/pendência (próxima ação relevante). */
export const precisaAcao = (n: Negociacao): boolean => {
    if (STATUS_ENCERRADOS.includes(n.status)) return false;
    if (isSemInteracao(n)) return true;
    if (isTermoPendente(n)) return true;
    if (isPendenciaDocumental(n)) return true;
    return false;
};

export interface ProximaVisitaInfo {
    data: string;       // YYYY-MM-DD
    modalidade: Modalidade;
}

/** Próxima visita agendada do lead (tarefa de visita ativa, hoje ou futura, mais próxima). */
export const proximaVisita = (n: Negociacao): ProximaVisitaInfo | null => {
    const tarefas: any[] = (n as any).tarefas || [];
    const hoje = isoHoje();
    const candidatas = tarefas
        .filter(t => t && t.tipo && !['concluido', 'cancelado'].includes(t.status))
        .filter(t => (t.vencimento || '').slice(0, 10) >= hoje)
        .sort((a, b) => (a.vencimento || '').localeCompare(b.vencimento || ''));
    if (candidatas.length === 0) return null;
    const t = candidatas[0];
    return { data: (t.vencimento || '').slice(0, 10), modalidade: normalizarModalidade(t.modalidade) };
};

export type WorkCounter = 'termos_pendentes' | 'pendencias_documentais' | 'sem_interacao' | 'simulacoes_hoje';
export type WorkChip = 'todos' | 'meu_trabalho' | 'pendencias_documentais' | 'termos_pendentes' | 'sem_interacao' | 'favoritos';

/** Predicado de um contador. */
export const matchCounter = (n: Negociacao, c: WorkCounter): boolean => {
    switch (c) {
        case 'termos_pendentes': return isTermoPendente(n);
        case 'pendencias_documentais': return isPendenciaDocumental(n);
        case 'sem_interacao': return isSemInteracao(n);
        case 'simulacoes_hoje': return entrouSimulacaoHoje(n);
    }
};

/** Predicado de um chip de filtro inteligente. */
export const matchChip = (n: Negociacao, chip: WorkChip): boolean => {
    switch (chip) {
        case 'todos': return true;
        case 'meu_trabalho': return isTermoPendente(n) || isPendenciaDocumental(n) || isSemInteracao(n);
        case 'pendencias_documentais': return isPendenciaDocumental(n);
        case 'termos_pendentes': return isTermoPendente(n);
        case 'sem_interacao': return isSemInteracao(n);
        case 'favoritos': return !!n.favorito;
    }
};

export interface WorkCounts {
    termos_pendentes: number;
    pendencias_documentais: number;
    sem_interacao: number;
    simulacoes_hoje: number;
    meta_hoje: number; // visitas concluídas hoje
}

/** Contadores da barra "Meu Trabalho Hoje" sobre um conjunto de negociações. */
export const computeWorkCounts = (negociacoes: Negociacao[]): WorkCounts => {
    const acc: WorkCounts = { termos_pendentes: 0, pendencias_documentais: 0, sem_interacao: 0, simulacoes_hoje: 0, meta_hoje: 0 };
    for (const n of negociacoes) {
        if (isTermoPendente(n)) acc.termos_pendentes++;
        if (isPendenciaDocumental(n)) acc.pendencias_documentais++;
        if (isSemInteracao(n)) acc.sem_interacao++;
        if (entrouSimulacaoHoje(n)) acc.simulacoes_hoje++;
        acc.meta_hoje += visitasConcluidasHoje(n);
    }
    return acc;
};

export const META_DIA = 10;
